/**
 * The signature of the homepage: seven real Mahjong tiles standing on a jade
 * slab, drifting the way pieces settle on a table.
 *
 * The tiles are the same vector components the game table uses - kikik's own
 * artwork, at a larger size - so the first thing a visitor sees is literally
 * the thing they are about to play with. Everything behind them (the slab, its
 * rim, the cut-paper fan) is CSS and inline SVG built here.
 *
 * The whole stage is one `role="img"`: assistive technology hears a single
 * sentence instead of seven tile names in a row.
 */

import type { CSSProperties } from "react";
import { MahjongTile } from "../../components/tiles";
import type { TileKind } from "@kikik/mahjong-core";

/** A pleasant, non-winning spread: three suits, a wind and the red dragon. */
const ARC: readonly TileKind[] = [
  "DOT5",
  "BAM3",
  "CHA8",
  "DRAGON_RED",
  "BAM1",
  "WIND_EAST",
  "DOT9",
];

/** Two pieces lying flat in front, so the arrangement is not a neat row. */
const LOOSE: readonly TileKind[] = ["CHA2", "DOT1"];

export interface TileStageProps {
  label: string;
}

export function TileStage({ label }: TileStageProps) {
  return (
    <div className="stage" role="img" aria-label={label}>
      <svg className="stage__fan" viewBox="0 0 400 400" aria-hidden="true" focusable="false">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => (
          <path
            key={index}
            className={`stage__wedge stage__wedge--${index % 2}`}
            d="M200 200 L200 0 A200 200 0 0 1 276.5 15.2 Z"
            transform={`rotate(${index * 45} 200 200)`}
          />
        ))}
      </svg>

      <div className="stage__slab" aria-hidden="true">
        <span className="stage__rim" />
      </div>

      <div className="stage__arc">
        {ARC.map((kind, index) => (
          <span key={kind} className="stage__tile" style={{ "--i": index } as CSSProperties}>
            <MahjongTile kind={kind} size="lg" />
          </span>
        ))}
      </div>

      <div className="stage__loose" aria-hidden="true">
        {LOOSE.map((kind, index) => (
          <span key={kind} className="stage__flat" style={{ "--i": index } as CSSProperties}>
            <MahjongTile kind={kind} size="md" />
          </span>
        ))}
      </div>
    </div>
  );
}
